
import { Component, Prop, Vue } from "vue-property-decorator";
import { actions, Equipment, Facility } from "@/survey";
import EquipmentForm from "@/views/EquipmentForm.vue";
import DisplayImagesCarousel from "@/components/DisplayImagesCarousel.vue";
import FileUploadInput from "@/components/FileUploadInput.vue";
import {
  booleanYNFormatter,
  dateFormatter,
  equipmentTypeFormatter,
  facilityFormatter
} from "@/components/formatters";
import ImageGallery from "@/components/ImageGallery.vue";
import { getFacilityById, getEquipmentById } from "@/store";
import { generateSpreadsheetSingleTableAndDownload } from "@/export/excel";
import { getEquipmentFieldsForExport, getEquipmentHeaderFields } from "@/export/util";
import PdfGenerator from "@/export/pdf";

@Component({
  components: {
    EquipmentForm,
    DisplayImagesCarousel: DisplayImagesCarousel,
    FileUploadInput,
    ImageGallery
  },
  watch: {
    filter() {
      this.$data.currentPage = 1;
    },
    columnFilters: {
      deep: true,
      handler() {
        this.$data.currentPage = 1;
      }
    },
    perPage() {
      this.$data.currentPage = 1;
    }
  }
})
export default class EquipmentTable extends Vue {
  apiLocation = Vue.prototype.$apiLocation;
  @Prop({ default: [] }) equipment?: Equipment[];
  @Prop({ default: { name: "N/A" } }) facility?: Facility | null;
  @Prop({ default: false }) isReport?: boolean;

  deleteEquipment(id: number, facilityId: number) {
    if (confirm("Confirm delete")) {
      this.$store.dispatch(actions.DELETE_EQUIPMENT, {
        id: id,
        facilityId: facilityId
      });
    }
  }

  get backTo() {
    return encodeURIComponent(this.$route.fullPath);
  }

  addImage(equipment: Equipment) {
    const payload = {
      image: this.$data.image,
      uploadName: this.$data.image.name,
      tableName: "equipment",
      recordId: equipment.id,
      success: () =>
        this.$store.dispatch(actions.GET_EQUIPMENT, {
          facility: equipment.facility
        })
    };
    this.$store.dispatch(actions.UPLOAD_FILE, payload);
  }

  getEquipmentById(id: number) {
    return getEquipmentById(id);
  }

  get tableData() {
    return this.$props.equipment || [];
  }

  get rowCount() {
    return this.filteredEquipment.length;
  }

  get canExport() {
    const user = this.$store.state.authentication.currentUser;
    return user !== null && (user.admin || user.export);
  }

  get filteredEquipment() {
    const globalFilter = this.normalize(this.$data.filter);
    const columnFilters = this.$data.columnFilters;
    return this.tableData.filter((equipment: any) => {
      if (globalFilter && !this.searchableText(equipment).includes(globalFilter)) {
        return false;
      }
      if (!this.matchesTextFilter(this.facilityName(equipment.facility), columnFilters.facility)) {
        return false;
      }
      if (!this.matchesTextFilter(equipment.name, columnFilters.name)) {
        return false;
      }
      if (!this.matchesTextFilter(this.equipmentTypeName(equipment.equipmentType), columnFilters.equipmentType)) {
        return false;
      }
      if (columnFilters.surveyed && String(equipment.surveyed === true) !== columnFilters.surveyed) {
        return false;
      }
      if (!this.matchesTextFilter(equipment.surveyTeam, columnFilters.surveyTeam)) {
        return false;
      }
      if (!this.matchesTextFilter(equipment.standardizedName, columnFilters.standardizedName)) {
        return false;
      }
      if (!this.matchesTextFilter(equipment.modelNo, columnFilters.modelNo)) {
        return false;
      }
      if (columnFilters.isLabeled && String(equipment.isLabeled === true) !== columnFilters.isLabeled) {
        return false;
      }
      if (!this.matchesTextFilter(this.tagText(equipment), columnFilters.tags)) {
        return false;
      }
      return true;
    });
  }

  get sortedFilteredEquipment() {
    const items = [...this.filteredEquipment];
    const sortBy = this.$data.sortBy;
    const sortDesc = this.$data.sortDesc;
    if (!sortBy) {
      return items;
    }
    return items.sort((first: any, second: any) => {
      const result = this.valueForSort(first, sortBy).localeCompare(
          this.valueForSort(second, sortBy),
          undefined,
          { numeric: true, sensitivity: "base" }
      );
      return sortDesc ? result * -1 : result;
    });
  }

  get equipmentTypeOptions() {
    const types = Array.from(new Set(this.tableData.map((equipment: Equipment) => this.equipmentTypeName(equipment.equipmentType))));
    return [{ value: "", text: "All" }].concat(
        types.filter(Boolean).sort().map((type: any) => ({ value: type, text: type }))
    );
  }

  get sortLabel() {
    const field = this.fields.find((item: any) => item.key === this.$data.sortBy);
    const direction = this.$data.sortDesc ? "descending" : "ascending";
    return `${field ? field.label || field.key : "Name"} (${direction})`;
  }

  data() {
    return {
      filter: "",
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      currentPage: 1,
      sortBy: "name",
      sortDesc: false,
      columnFilters: {
        facility: "",
        name: "",
        equipmentType: "",
        surveyed: "",
        surveyTeam: "",
        standardizedName: "",
        modelNo: "",
        isLabeled: "",
        tags: ""
      },
      surveyedOptions: [
        { value: "", text: "All" },
        { value: "true", text: "Yes" },
        { value: "false", text: "No" }
      ],
      labeledOptions: [
        { value: "", text: "All" },
        { value: "true", text: "Labeled" },
        { value: "false", text: "Unlabeled" }
      ],
      image: null,
      mainImage: false,
      component: this
    };
  }

  closeModal(id: string) {
    this.$bvModal.hide(id);
  }

  get fields() {
    const theFields: any[] = [
      { key: "name", sortable: true },
      {
        key: "equipmentType",
        label: "Equipment Type",
        sortable: true,
        formatter: equipmentTypeFormatter(this.$store.state.equipmentTypes)
      },
      { key: "surveyed", sortable: true, formatter: booleanYNFormatter },
      { key: "surveyTeam", sortable: true },
      { key: "standardizedName", sortable: true },
      { key: "modelNo", sortable: true },
      // { key: "ipAddress", sortable: true },
      { key: "labels", sortable: false },
      { key: "tags", sortable: false },
      { key: "created", sortable: true, formatter: dateFormatter() },
      { key: "updated", sortable: true, formatter: dateFormatter() }
    ];
    if (this.$props.isReport) {
      theFields.splice(1, 0, {
        key: "facility",
        sortable: true,
        formatter: facilityFormatter(getFacilityById)
      });
    }
    return theFields;
  }

  clearFilters() {
    this.$data.filter = "";
    Object.keys(this.$data.columnFilters).forEach(key => {
      this.$data.columnFilters[key] = "";
    });
    this.$data.currentPage = 1;
  }

  exportPdf() {
    new PdfGenerator()
        .getEquipmentListTable(this.sortedFilteredEquipment, this.$store.state.equipmentTypes, this.$store.state.facilities)
        .then((pdfDoc: any) => {
          pdfDoc.download("equipment-list.pdf");
        });
  }

  exportExcel() {
    const data = [getEquipmentHeaderFields()].concat(
        getEquipmentFieldsForExport(this.sortedFilteredEquipment, this.$store.state.equipmentTypes)
    );
    generateSpreadsheetSingleTableAndDownload(data, "equipment-list.xlsx");
  }

  matchesTextFilter(value: any, filterValue: string) {
    const filter = this.normalize(filterValue);
    if (!filter) {
      return true;
    }
    return this.normalize(value).includes(filter);
  }

  normalize(value: any) {
    return `${value === undefined || value === null ? "" : value}`.toLowerCase();
  }

  searchableText(equipment: any) {
    return this.normalize([
      equipment.name,
      this.facilityName(equipment.facility),
      this.equipmentTypeName(equipment.equipmentType),
      equipment.surveyed === true ? "yes y surveyed" : "no n not surveyed",
      equipment.surveyTeam,
      equipment.standardizedName,
      equipment.modelNo,
      equipment.serialNo,
      equipment.macAddress,
      equipment.location,
      equipment.description,
      equipment.isLabeled === true ? "labeled" : "unlabeled",
      equipment.damaged === true ? "damaged" : "",
      this.tagText(equipment)
    ].join(" "));
  }

  tagText(equipment: any) {
    return (equipment.tags || []).map((tag: any) => tag.tag).join(" ");
  }

  equipmentTypeName(value: number) {
    return equipmentTypeFormatter(this.$store.state.equipmentTypes)(value);
  }

  facilityName(value: number) {
    const facility = getFacilityById(value);
    return facility === undefined ? "Unknown" : facility.name;
  }

  valueForSort(item: any, key: string) {
    if (key === "equipmentType") {
      return this.equipmentTypeName(item.equipmentType);
    }
    if (key === "facility") {
      return this.facilityName(item.facility);
    }
    if (key === "surveyed") {
      return item.surveyed === true ? "Y" : "N";
    }
    if (key === "labels") {
      return item.isLabeled === true ? "labeled" : "unlabeled";
    }
    if (key === "tags") {
      return this.tagText(item);
    }
    return `${item[key] === undefined || item[key] === null ? "" : item[key]}`;
  }

  sortCompare(firstItem: any, secondItem: any, key: string) {
    return this.valueForSort(firstItem, key).localeCompare(
        this.valueForSort(secondItem, key),
        undefined,
        { numeric: true, sensitivity: "base" }
    );
  }
}
